import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, MapPin, Search, SlidersHorizontal, X } from "lucide-react";
import { api } from "../api";
import type { Client, DisplaySettings } from "../types";
import type { MapCamera, MapCanvasHandle } from "./MapCanvas";
import MapFrameHost from "./MapFrameHost";
import { FilterPanel } from "./ProjectDiscovery";
import {
  emptyProjectFilters, filterProjects, projectMatchesFilters,
  transitionGallery, transitionVideoExit,
  type GalleryTransitionState, type ProjectFilters, type VideoExitState
} from "../displayInteractions";
import { useResilientVideoPlayback } from "../hooks/useResilientVideoPlayback";
import { videoUrlIsHealthy } from "../videoHealth";
import { cacheVideoForOffline } from "../offlineMedia";
import { overlayThemeVariables } from "../overlayTheme";

const defaultSettings: DisplaySettings = {
  inactivityTimeoutSeconds: 60, galleryInactivityTimeoutSeconds: 300, inactivityVideoEnabled: false, inactivityVideoPath: null,
  mapCenterLatitude: 37.09, mapCenterLongitude: -8.24, mapZoom: 9, markerLabelMinZoom: 11,
  logoPath: null, brandTitle: "Algarve Projects", brandSubtitle: "Windows & doors",
  accentColor: "#9c6f3c", displayLoadingText: "Preparing the Algarve",
  touchHintText: "Touch a project to explore", offlineText: "Offline · showing saved projects",
  galleryFallbackLocation: "Algarve, Portugal", galleryEmptyTitle: "Images coming soon",
  imageUnavailableText: "Image unavailable", screensaverPromptText: "Touch anywhere to explore our projects",
  mapLabel: "Map", satelliteLabel: "Satellite", overlayEnabled: false, overlayUrl: null,
  overlayButtonLabel: "Explore more", overlayTitle: "More information", assetLinksJson: "[]",
  performanceMode: true, overlayActionBackgroundColor: "#f7f5ef", mapDimStrength: 35, optimizedTileLoading: true, updatedAt: ""
};

export default function Display() {
  const [clients, setClients] = useState<Client[]>([]);
  const [settings, setSettings] = useState(defaultSettings);
  const [selected, setSelected] = useState<Client | null>(null);
  const [videoExitState, setVideoExitState] = useState<VideoExitState>("hidden");
  const [videoSource, setVideoSource] = useState<string | null>(null);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [ready, setReady] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [filters, setFilters] = useState<ProjectFilters>(emptyProjectFilters);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlightedClientId, setHighlightedClientId] = useState<number | null>(null);
  const [mapWaking, setMapWaking] = useState(false);
  const [galleryState, setGalleryState] = useState<GalleryTransitionState>("closed");
  const timer = useRef<number | null>(null);
  const frame = useRef<number | null>(null);
  const transitionTimer = useRef<number | null>(null);
  const recoveryRefreshTimer = useRef<number | null>(null);
  const pendingRecoveryRefresh = useRef(false);
  const lastActivityAt = useRef(Date.now());
  const mapCanvas = useRef<MapCanvasHandle | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  const videoPreloader = useRef<HTMLVideoElement | null>(null);
  const galleryFrame = useRef<HTMLIFrameElement | null>(null);
  const galleryFrameReady = useRef(false);
  const gallerySentClientId = useRef<number | null>(null);
  const searchFrame = useRef<HTMLIFrameElement | null>(null);
  const galleryStateRef = useRef<GalleryTransitionState>("closed");
  const galleryMapCamera = useRef<MapCamera | null>(null);
  const offlineRef = useRef(offline);
  const uiBusyRef = useRef(false);
  const videoDebug = import.meta.env.VITE_VIDEO_DEBUG === "true";
  const videoDiagnostic = useResilientVideoPlayback({
    videoRef: video, active: videoExitState === "active", source: videoSource, debug: videoDebug
  });
  const filteredClients = useMemo(() => filterProjects(clients, filters), [clients, filters]);
  const visibleClientIds = useMemo(() => new Set(filteredClients.map((client) => client.id)), [filteredClients]);
  galleryStateRef.current = galleryState;
  offlineRef.current = offline;
  uiBusyRef.current = galleryState !== "closed" || filterOpen || searchOpen || overlayOpen || videoExitState === "exiting";

  const refresh = useCallback(async () => {
    try {
      const [nextClients, nextSettings] = await Promise.all([
        api<Client[]>("/api/clients"), api<DisplaySettings>("/api/settings")
      ]);
      setClients(nextClients);
      setSettings({ ...defaultSettings, ...nextSettings });
      offlineRef.current = !navigator.onLine;
      setOffline(!navigator.onLine);
      setReady(true);
      setSelected((current) => current ? nextClients.find((client) => client.id === current.id) || null : null);
    } catch {
      offlineRef.current = true;
      setOffline(true);
      setReady(true);
    }
  }, []);

  const scheduleRecoveryRefresh = useCallback(() => {
    pendingRecoveryRefresh.current = true;
    if (recoveryRefreshTimer.current) window.clearTimeout(recoveryRefreshTimer.current);

    const refreshWhenIdle = async () => {
      if (!navigator.onLine) {
        recoveryRefreshTimer.current = null;
        return;
      }
      const remainingIdleTime = 3_000 - (Date.now() - lastActivityAt.current);
      if (uiBusyRef.current || remainingIdleTime > 0) {
        recoveryRefreshTimer.current = window.setTimeout(refreshWhenIdle, uiBusyRef.current ? 3_000 : Math.max(250, remainingIdleTime));
        return;
      }

      try {
        const response = await fetch("/api/health", { cache: "no-store" });
        if (!response.ok) throw new Error("The server is not ready");
        const remainingAfterCheck = 3_000 - (Date.now() - lastActivityAt.current);
        if (uiBusyRef.current || remainingAfterCheck > 0) {
          recoveryRefreshTimer.current = window.setTimeout(refreshWhenIdle, uiBusyRef.current ? 3_000 : Math.max(250, remainingAfterCheck));
          return;
        }
        recoveryRefreshTimer.current = null;
        pendingRecoveryRefresh.current = false;
        window.location.reload();
      } catch {
        recoveryRefreshTimer.current = window.setTimeout(refreshWhenIdle, 5_000);
      }
    };

    recoveryRefreshTimer.current = window.setTimeout(refreshWhenIdle, 3_000);
  }, []);

  const resetActivity = useCallback(() => {
    lastActivityAt.current = Date.now();
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      if (pendingRecoveryRefresh.current) scheduleRecoveryRefresh();
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = null;
      if (overlayOpen) return;
      timer.current = window.setTimeout(() => {
        if (settings.inactivityVideoEnabled && settings.inactivityVideoPath) {
          setVideoExitState((state) => transitionVideoExit(state, "show"));
        }
      }, Math.max(10, galleryStateRef.current === "open" ? settings.galleryInactivityTimeoutSeconds : settings.inactivityTimeoutSeconds) * 1000);
    });
  }, [overlayOpen, scheduleRecoveryRefresh, settings.galleryInactivityTimeoutSeconds, settings.inactivityTimeoutSeconds, settings.inactivityVideoEnabled, settings.inactivityVideoPath]);

  useEffect(() => {
    if (video.current) {
      video.current.muted = true;
      video.current.defaultMuted = true;
    }
    if (videoPreloader.current) {
      videoPreloader.current.muted = true;
      videoPreloader.current.defaultMuted = true;
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    const stream = new EventSource("/api/events");
    stream.addEventListener("update", () => void refresh());
    stream.onopen = () => {
      if (offlineRef.current && navigator.onLine) scheduleRecoveryRefresh();
    };
    stream.onerror = () => {
      offlineRef.current = true;
      setOffline(true);
    };
    return () => stream.close();
  }, [refresh, scheduleRecoveryRefresh]);
  useEffect(() => {
    const nextSource = settings.inactivityVideoPath;
    const preload = videoPreloader.current;
    if (!nextSource) {
      setVideoSource(null);
      return;
    }
    if (!preload || nextSource === videoSource) return;

    const controller = new AbortController();
    let cancelled = false;
    let timeout: number | null = null;
    let releaseTimer: number | null = null;

    const prepare = async () => {
      const healthy = await videoUrlIsHealthy(nextSource, fetch, controller.signal);
      if (!healthy || cancelled) {
        return;
      }

      preload.muted = true;
      preload.defaultMuted = true;
      preload.preload = "auto";
      preload.playsInline = true;
      preload.src = nextSource;

      await new Promise<void>((resolve, reject) => {
        const finish = (error?: Error) => {
          if (timeout) window.clearTimeout(timeout);
          preload.removeEventListener("canplay", onCanPlay);
          preload.removeEventListener("error", onError);
          error ? reject(error) : resolve();
        };
        const onCanPlay = () => finish();
        const onError = () => finish(new Error("Preloaded video could not play"));
        preload.addEventListener("canplay", onCanPlay, { once: true });
        preload.addEventListener("error", onError, { once: true });
        timeout = window.setTimeout(() => finish(new Error("Video preload timed out")), 20_000);
        preload.load();
      });

      if (cancelled) return;
      setVideoSource(nextSource);
      void cacheVideoForOffline(nextSource);
      releaseTimer = window.setTimeout(() => {
        preload.pause();
        preload.removeAttribute("src");
        preload.load();
      }, 10_000);
    };

    void prepare().catch(() => undefined);

    return () => {
      cancelled = true;
      controller.abort();
      if (timeout) window.clearTimeout(timeout);
      if (releaseTimer) window.clearTimeout(releaseTimer);
      preload.pause();
      preload.removeAttribute("src");
      preload.load();
    };
  }, [settings.inactivityVideoPath, videoDebug, videoSource]);
  useEffect(() => {
    const contactEvent = "PointerEvent" in window ? "pointerdown" : "touchstart";
    const events = [contactEvent, "wheel", "keydown"];
    events.forEach((event) => window.addEventListener(event, resetActivity, { passive: true }));
    resetActivity();
    return () => {
      events.forEach((event) => window.removeEventListener(event, resetActivity));
      if (timer.current) window.clearTimeout(timer.current);
      if (frame.current) {
        cancelAnimationFrame(frame.current);
        frame.current = null;
      }
      if (transitionTimer.current) window.clearTimeout(transitionTimer.current);
      if (recoveryRefreshTimer.current) {
        window.clearTimeout(recoveryRefreshTimer.current);
        recoveryRefreshTimer.current = null;
      }
    };
  }, [resetActivity]);
  useEffect(() => {
    const online = () => scheduleRecoveryRefresh();
    const gone = () => {
      offlineRef.current = true;
      setOffline(true);
    };
    window.addEventListener("online", online);
    window.addEventListener("offline", gone);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", gone);
    };
  }, [scheduleRecoveryRefresh]);
  useEffect(() => {
    if (!offline) return;
    let cancelled = false;
    const probeConnection = async () => {
      if (!navigator.onLine) return;
      try {
        const response = await fetch("/api/health", { cache: "no-store" });
        if (!cancelled && response.ok) scheduleRecoveryRefresh();
      } catch {
        // Keep showing locally cached content and try again later.
      }
    };
    void probeConnection();
    const interval = window.setInterval(() => void probeConnection(), 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [offline, scheduleRecoveryRefresh]);
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    const acquire = async () => {
      try { if ("wakeLock" in navigator) lock = await navigator.wakeLock.request("screen"); } catch { /* optional */ }
    };
    const visible = () => { if (document.visibilityState === "visible") void acquire(); };
    void acquire();
    document.addEventListener("visibilitychange", visible);
    return () => {
      document.removeEventListener("visibilitychange", visible);
      void lock?.release();
      lock = null;
    };
  }, []);
  useEffect(() => {
    if (!selected) {
      if (galleryStateRef.current !== "closed") {
        galleryStateRef.current = "closed";
        setGalleryState("closed");
        setHighlightedClientId(null);
      }
    }
  }, [selected]);

  useEffect(() => {
    if (!settings.overlayEnabled || !settings.overlayUrl) setOverlayOpen(false);
  }, [settings.overlayEnabled, settings.overlayUrl]);

  const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const completeGalleryClose = () => {
    galleryFrame.current?.contentWindow?.postMessage({ type: "SHOWROOM_GALLERY_CLEAR" }, window.location.origin);
    gallerySentClientId.current = null;
    if (galleryMapCamera.current) {
      mapCanvas.current?.restoreCamera(galleryMapCamera.current);
      galleryMapCamera.current = null;
    }
    setSelected(null);
    setHighlightedClientId(null);
    galleryStateRef.current = "closed";
    setGalleryState((state) => transitionGallery(state, "closed"));
  };
  const openOverlay = () => {
    setVideoExitState("hidden");
    setSelected(null);
    galleryMapCamera.current = null;
    galleryStateRef.current = "closed";
    setGalleryState("closed");
    setOverlayOpen(true);
  };
  const closeOverlay = () => setOverlayOpen(false);
  const openClient = useCallback((client: Client) => {
    if (galleryStateRef.current !== "closed") return;
    galleryStateRef.current = "opening";
    gallerySentClientId.current = null;
    galleryMapCamera.current = mapCanvas.current?.getCamera() || null;
    setFilterOpen(false);
    setSearchOpen(false);
    setSelected(client);
    setHighlightedClientId(client.id);
    setGalleryState((state) => transitionGallery(state, "open"));
    resetActivity();
  }, [resetActivity]);
  const sendGalleryClient = useCallback(() => {
    if (!selected || galleryStateRef.current === "closed") return;
    if (!galleryFrameReady.current) return;
    if (gallerySentClientId.current === selected.id) return;
    gallerySentClientId.current = selected.id;
    galleryFrame.current?.contentWindow?.postMessage({
      type: "SHOWROOM_GALLERY_OPEN",
      clientId: selected.id,
      client: selected,
      settings
    }, window.location.origin);
  }, [selected, settings]);
  const sendSearchData = useCallback(() => {
    if (!searchOpen) return;
    searchFrame.current?.contentWindow?.postMessage({
      type: "SHOWROOM_SEARCH_OPEN",
      filteredClients,
      overlayActionBackgroundColor: settings.overlayActionBackgroundColor
    }, window.location.origin);
  }, [filteredClients, searchOpen, settings.overlayActionBackgroundColor]);
  const closeGallery = () => {
    if (!selected || (galleryStateRef.current !== "open" && galleryStateRef.current !== "opening")) return;
    galleryStateRef.current = "closing";
    setGalleryState((state) => state === "opening" ? "closing" : transitionGallery(state, "close"));
    if (document.fullscreenElement) void document.exitFullscreen();
    transitionTimer.current = window.setTimeout(completeGalleryClose, reducedMotion() ? 60 : 200);
  };
  useEffect(() => {
    const receiveGalleryMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "SHOWROOM_GALLERY_ACTIVITY") resetActivity();
      if (event.data?.type === "SHOWROOM_GALLERY_CLOSE") closeGallery();
      if (event.data?.type === "SHOWROOM_GALLERY_READY") {
        galleryFrameReady.current = true;
        sendGalleryClient();
      }
    };
    window.addEventListener("message", receiveGalleryMessage);
    return () => window.removeEventListener("message", receiveGalleryMessage);
  });
  const closeScreensaver = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (videoExitState !== "active") return;
    setVideoExitState((state) => transitionVideoExit(state, "touch"));
    setFilterOpen(false);
    setSearchOpen(false);
    setOverlayOpen(false);
    setSelected(null);
    galleryMapCamera.current = null;
    galleryStateRef.current = "closed";
    setGalleryState("closed");
    const reduced = reducedMotion();
    setMapWaking(!reduced);
    mapCanvas.current?.resetHome(
      { lat: settings.mapCenterLatitude, lng: settings.mapCenterLongitude },
      settings.mapZoom, reduced
    );
    transitionTimer.current = window.setTimeout(() => {
      video.current?.pause();
      if (video.current) video.current.currentTime = 0;
      setVideoExitState((state) => transitionVideoExit(state, "complete"));
      setMapWaking(false);
      resetActivity();
    }, reduced ? 100 : 760);
  };
  const selectSearchResult = async (client: Client) => {
    if (!projectMatchesFilters(client, filters)) setFilters(emptyProjectFilters);
    setSearchOpen(false);
    setFilterOpen(false);
    setHighlightedClientId(client.id);
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve())));
    await mapCanvas.current?.focusClient(client, Math.max(12, settings.mapZoom));
    openClient(client);
  };
  useEffect(() => {
    const receiveSearchMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "SHOWROOM_SEARCH_ACTIVITY") resetActivity();
      if (event.data?.type === "SHOWROOM_SEARCH_CLOSE") {
        searchFrame.current?.contentWindow?.postMessage({ type: "SHOWROOM_SEARCH_CLEAR" }, window.location.origin);
        setSearchOpen(false);
        resetActivity();
      }
      if (event.data?.type === "SHOWROOM_SEARCH_READY") sendSearchData();
      if (event.data?.type === "SHOWROOM_SEARCH_SELECT") {
        const client = clients.find((item) => item.id === Number(event.data.clientId));
        if (client) void selectSearchResult(client);
      }
    };
    window.addEventListener("message", receiveSearchMessage);
    return () => window.removeEventListener("message", receiveSearchMessage);
  }, [clients, resetActivity, sendSearchData]);

  useEffect(() => { sendSearchData(); }, [sendSearchData]);

  useEffect(() => {
    if (!searchOpen) searchFrame.current?.contentWindow?.postMessage({ type: "SHOWROOM_SEARCH_CLEAR" }, window.location.origin);
  }, [searchOpen]);

  useEffect(() => {
    if (galleryState !== "opening" || !selected) return;
    const timeout = window.setTimeout(() => {
      galleryStateRef.current = "open";
      setGalleryState((state) => transitionGallery(state, "opened"));
      resetActivity();
    }, reducedMotion() ? 40 : 140);
    return () => window.clearTimeout(timeout);
  }, [galleryState, selected]);

  useEffect(() => { sendGalleryClient(); }, [sendGalleryClient, galleryState]);

  useEffect(() => {
    if (selected && !visibleClientIds.has(selected.id) && galleryState === "open") closeGallery();
  }, [filters, galleryState, selected, visibleClientIds]);

  const mainClass = "display" + (galleryState !== "closed" ? " gallery-active" : "") + (mapWaking ? " map-waking" : "") + (settings.performanceMode ? " performance-mode" : "");

  return <main className={mainClass} style={{
    ...overlayThemeVariables(settings.overlayActionBackgroundColor),
    "--accent": settings.accentColor,
  } as React.CSSProperties}>
    <MapFrameHost ref={mapCanvas} clients={clients} center={{ lat: settings.mapCenterLatitude, lng: settings.mapCenterLongitude }}
      zoom={settings.mapZoom} markerLabelMinZoom={settings.markerLabelMinZoom}
      mapLabel={settings.mapLabel} satelliteLabel={settings.satelliteLabel}
      accentColor={settings.accentColor} overlayActionBackgroundColor={settings.overlayActionBackgroundColor}
      mapDimStrength={settings.mapDimStrength} optimizedTileLoading={settings.optimizedTileLoading}
      visibleClientIds={visibleClientIds} highlightedClientId={highlightedClientId}
      onSelect={openClient} onActivity={resetActivity} />

    <div className="discovery-controls" aria-label="Find projects">
      <button className={(filters.productType || filters.style ? "active " : "") + "discovery-button"}
        onPointerDown={() => { setFilterOpen((open) => !open); setSearchOpen(false); resetActivity(); }}>
        <SlidersHorizontal /><span>Filter projects</span>
        {(filters.productType || filters.style) && <i>{filteredClients.length}</i>}
      </button>
      <button className="discovery-button" onPointerDown={() => { setSearchOpen(true); setFilterOpen(false); resetActivity(); }}>
        <Search /><span>Search</span>
      </button>
    </div>
    {filterOpen && <FilterPanel filters={filters} count={filteredClients.length}
      onChange={(next) => { setFilters(next); resetActivity(); }} onClose={() => setFilterOpen(false)} />}
    <section className={"search-iframe-overlay " + (searchOpen ? "is-open" : "")} aria-hidden={!searchOpen}>
      <iframe ref={searchFrame} className="search-iframe" title="Search projects" src="/search-frame" loading="eager" />
    </section>

    <header className="display-header">
      <div className="brand-mark">
        {settings.logoPath
          ? <img src={settings.logoPath} alt={settings.brandTitle + " logo"} />
          : <span className="brand-monogram">{settings.brandTitle.charAt(0) || "A"}</span>}
        <div><span className="eyebrow">{settings.brandSubtitle}</span><strong>{settings.brandTitle}</strong></div>
      </div>
      <div className="display-header-actions">
        <div className="touch-hint"><MapPin /><span>{settings.touchHintText}</span></div>
        {settings.overlayEnabled && settings.overlayUrl && <button className="display-link-button" onClick={openOverlay}>
          <ExternalLink /><span>{settings.overlayButtonLabel}</span>
        </button>}
      </div>
    </header>

    {!ready && <div className="display-loader"><span /><p>{settings.displayLoadingText}</p></div>}
    {offline && <div className="offline-pill">{settings.offlineText}</div>}

    <section className={"gallery-iframe-overlay is-" + galleryState} aria-hidden={galleryState === "closed"}>
      <iframe
        ref={galleryFrame}
        className="gallery-iframe"
        title={(selected?.name || "Project") + " gallery"}
        src="/gallery-frame"
        loading="eager" />
    </section>


    {overlayOpen && settings.overlayUrl && <section className="web-overlay" aria-label={settings.overlayTitle}>
      <header><h2>{settings.overlayTitle}</h2><button onClick={closeOverlay} aria-label="Close"><X /></button></header>
      <iframe src={settings.overlayUrl} title={settings.overlayTitle}
        scrolling="yes"
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin" />
    </section>}
    <video ref={videoPreloader} className="video-preloader" muted playsInline preload="auto"
      controls={false} disablePictureInPicture disableRemotePlayback aria-hidden="true" />
    <div className={"screensaver is-" + videoExitState} onPointerDown={closeScreensaver}
      aria-hidden={videoExitState === "hidden"}>
      <video ref={video} autoPlay muted loop playsInline preload="auto" controls={false}
        disablePictureInPicture disableRemotePlayback />
      <div className="screensaver-shade" />
      <div className="screensaver-copy">
        <span className="brand-monogram light">{settings.brandTitle.charAt(0) || "A"}</span>
        <span>{settings.screensaverPromptText}</span>
      </div>
      {videoDebug && <div className="video-debug" aria-hidden="true">
        <span>time {videoDiagnostic.currentTime.toFixed(1)}</span>
        <span>ready {videoDiagnostic.readyState}</span>
        <span>network {videoDiagnostic.networkState}</span>
        <span>paused {String(videoDiagnostic.paused)}</span>
        <span>{videoDiagnostic.watchdogStatus}</span>
        <span>{videoDiagnostic.eventName}</span>
        <span>recovery {videoDiagnostic.recoveryAttempt}</span>
      </div>}
    </div>
  </main>;
}
